import { Check, ChevronDown, ChevronUp, Copy, Pencil } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import { useEditorContext } from '@/components/demands-v2/editor/context/editor-provider';
import { Button } from '@/components/ui/catalyst/button';
import { Text } from '@/components/ui/catalyst/text';
import { useGetProposedEditQuery } from '@/services/api/ckeditorService';

import { MessageContentViewer } from '../MessageContentViewer';
import { BaseBadge } from './BaseBadge';
import { BaseToolBadgeProps } from './types';

const EditDocumentResponseSchema = z.object({
  proposed_edit_id: z.string(),
});

const EditDocumentBadgeContent = ({
  documentId,
  proposedEditId,
}: {
  documentId: string;
  proposedEditId: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [contentExceedsMaxHeight, setContentExceedsMaxHeight] = useState(false);
  const [oldContentCopied, setOldContentCopied] = useState(false);
  const [newContentCopied, setNewContentCopied] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const { data: proposedEdit } = useGetProposedEditQuery({
    documentId,
    proposedEditId,
  });

  const toggleOpen = () => setIsOpen((prev) => !prev);

  useEffect(() => {
    if (contentRef.current && proposedEdit?.new_content) {
      // 80px is equivalent to max-h-20 in Tailwind
      const maxHeight = 80;
      const contentHeight = contentRef.current.scrollHeight;
      setContentExceedsMaxHeight(contentHeight > maxHeight);
    }
  }, [proposedEdit?.new_content]);

  const copyContent = (content: string, isOld: boolean) => {
    navigator.clipboard
      .write([
        new ClipboardItem({
          'text/html': content,
        }),
      ])
      .then(() => {
        if (isOld) {
          setOldContentCopied(true);
          setTimeout(() => setOldContentCopied(false), 2000);
        } else {
          setNewContentCopied(true);
          setTimeout(() => setNewContentCopied(false), 2000);
        }
      })
      .catch(console.error);
  };

  return (
    <div className="flex flex-col border border-border rounded-md p-2 bg-background">
      <div className="flex flex-row gap-2 items-center justify-start">
        <div className="flex flex-row gap-2 items-center">
          <div className="relative inline-flex items-center justify-center animate-pulse">
            <Pencil
              className="w-4 h-4"
              style={{
                color: '#393BB2',
                filter: 'drop-shadow(0 0 1px #E2CBFF)',
              }}
            />
          </div>

          <Text className="text-sm font-medium">Suggested Edit</Text>
        </div>
      </div>
      <div
        ref={contentRef}
        className={`flex flex-col gap-4 relative ${contentExceedsMaxHeight && !isOpen ? 'max-h-20 overflow-hidden' : ''}`}
      >
        {proposedEdit?.old_content && (
          <div className="flex flex-col gap-1">
            <div className="rounded-lg shadow-sm border border-red-200 dark:border-red-800/30 overflow-hidden transition-all">
              <div className="flex justify-between items-center px-4 py-2 border-b border-red-200 dark:border-red-800/30 bg-red-50/80 dark:bg-red-900/20 backdrop-blur-sm">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <Text className="text-sm font-medium text-red-600 dark:text-red-400">
                    Old Content
                  </Text>
                </div>
                <Button
                  plain
                  onClick={() => copyContent(proposedEdit.old_content, true)}
                  className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-full transition-colors"
                  aria-label="Copy old content"
                >
                  {oldContentCopied ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4 text-red-500/70" />
                  )}
                </Button>
              </div>
              <style>
                {`
                  .old-content-viewer * {
                    background-color: #fef2f2 !important;
                    line-height: normal !important;
                  }
                  .old-content-viewer {
                    transition: all 0.2s ease;
                    background-color: #fef2f2;
                  }
                  .old-content-viewer table {
                    background-color: transparent !important;
                    border-color: #fecaca !important;
                  }
                  .old-content-viewer table th,
                  .old-content-viewer table td {
                    background-color: #fef2f2 !important;
                    border-color: #fecaca !important;
                  }
                  .old-content-viewer table tr:nth-child(odd) td {
                    background-color: #fef2f2 !important;
                  }
                  .old-content-viewer pre {
                    background-color: #fee2e2 !important;
                  }
                `}
              </style>
              <div className="old-content-viewer p-4">
                <MessageContentViewer content={proposedEdit.old_content} />
              </div>
            </div>
          </div>
        )}

        {proposedEdit?.new_content && (
          <div className="flex flex-col gap-1">
            <div className="rounded-lg shadow-sm border border-green-200 dark:border-green-800/30 overflow-hidden transition-all">
              <div className="flex justify-between items-center px-4 py-2 border-b border-green-200 dark:border-green-800/30 bg-green-50/80 dark:bg-green-900/20 backdrop-blur-sm">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <Text className="text-sm font-medium text-green-600 dark:text-green-400">
                    New Content
                  </Text>
                </div>
                <Button
                  plain
                  onClick={() => copyContent(proposedEdit.new_content, false)}
                  className="p-1 hover:bg-green-100 dark:hover:bg-green-900/20 rounded-full transition-colors"
                  aria-label="Copy new content"
                >
                  {newContentCopied ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4 text-green-500/70" />
                  )}
                </Button>
              </div>
              <style>
                {`
                  .new-content-viewer * {
                    background-color: #f0f9f0 !important;
                    line-height: normal !important;
                  }
                  .new-content-viewer {
                    transition: all 0.2s ease;
                    background-color: #f0f9f0;
                  }
                  .new-content-viewer table {
                    background-color: transparent !important;
                    border-color: #d1fad1 !important;
                  }
                  .new-content-viewer table th,
                  .new-content-viewer table td {
                    background-color: #f0f9f0 !important;
                    border-color: #d1fad1 !important;
                  }
                  .new-content-viewer table tr:nth-child(odd) td {
                    background-color: #f0f9f0 !important;
                  }
                  .new-content-viewer pre {
                    background-color: #e6f7e6 !important;
                  }
                `}
              </style>
              <div className="new-content-viewer p-4">
                <MessageContentViewer content={proposedEdit.new_content} />
              </div>
            </div>
          </div>
        )}

        {contentExceedsMaxHeight && !isOpen && (
          <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent" />
        )}
      </div>
      {contentExceedsMaxHeight && (
        <Button
          plain
          onClick={toggleOpen}
          className="w-full mt-1 py-1"
          aria-label="Show more"
        >
          {isOpen ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </Button>
      )}
    </div>
  );
};

export const EditDocumentBadge = ({ isDone, ...props }: BaseToolBadgeProps) => {
  const allProps = { ...props, isDone };

  const edit = useMemo(() => {
    if (!props.toolMessage) return null;
    try {
      if (typeof props.toolMessage.content !== 'string') {
        return null;
      }
      const parsed = JSON.parse(props.toolMessage.content);
      const validated = EditDocumentResponseSchema.safeParse(parsed);

      if (validated.success) {
        return validated.data;
      }
      return null;
    } catch (error) {
      return null;
    }
  }, [props.toolMessage]);

  const { editor } = useEditorContext();

  const baseBadge = (
    <BaseBadge
      {...allProps}
      showArgs={false}
      resultTexts={{
        loading: 'Editing document...',
        done: 'Document edited',
        error: 'Error editing document',
      }}
    />
  );

  if (!edit || !editor) {
    return baseBadge;
  }

  return (
    <div className="flex flex-col gap-2 w-full">
      <EditDocumentBadgeContent
        documentId={editor.config.get('documentId') as string}
        proposedEditId={edit.proposed_edit_id}
      />
    </div>
  );
};
